import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Scrolls a target DOM node into view and applies the global
 * `blink-highlight` animation for ~2.5s when the page is opened
 * with a `?highlight=<key>` query param.
 *
 * Used by portal pages that receive deep-links from notifications
 * (doc-expiry, memo, salary-advance, appraisal cycle, etc).
 *
 * @param {Object} refMap        keyed map of refs (key -> DOM element)
 * @param {boolean} ready        page-specific gate (e.g. !loading && data)
 * @param {function} [onMatch]   optional side-effect to run when a match
 *                               is found (e.g. expand a collapsed section)
 */
export default function useBlinkHighlight(refMap, ready, onMatch) {
  const [searchParams] = useSearchParams();
  const highlight = (searchParams.get('highlight') || '').trim();

  useEffect(() => {
    if (!highlight || !ready) return;
    let cancelled = false;
    const t = setTimeout(() => {
      if (cancelled) return;
      const el = refMap.current?.[highlight];
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('blink-highlight');
      setTimeout(() => el.classList.remove('blink-highlight'), 2600);
      if (typeof onMatch === 'function') onMatch(highlight);
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlight, ready]);

  return highlight;
}
